import React, { useMemo } from "react";
import { Text, View, Svg, Circle, Path } from "@react-pdf/renderer";
import { withNamespaces } from "react-i18next";

import DateUtils from "src/services/utils/DateUtils";
import { INTERNATIONAL_DATE_FORMAT } from "src/common/constants";
import { HistoryLogDescriptionCreator } from "src/modules/3rd-party-management/services/HistoryLogDescriptionCreator";

import styles from "../../styles";
import { buildManagementSummaryData } from "../../helpers/managementSummary";
import { formatDisplayValue } from "../../helpers";
import { PageReference } from "../../shared/PageReference";

const logDescriptionCreator = new HistoryLogDescriptionCreator();

const getPercentages = (items = []) => {
    const total = items.reduce((sum, item) => sum + Number(item.amount || 0), 0);

    if (!total) {
        return items.map((item) => ({ ...item, percentage: 0 }));
    }

    return items.map((item) => ({
        ...item,
        percentage: Math.round((Number(item.amount || 0) / total) * 100),
    }));
};

const ICON_COLOR = "#E06A78";

const SummaryIcon = ({ name }) => {
    const paths = {
        building: "M4 20V9.5L12 4l8 5.5V20h-5v-5H9v5H4zm3-2h1.2v-2.2H7V18zm4.4 0H13v-2.2h-1.6V18z",
        calendar: "M7 3.5v2M17 3.5v2M4.5 8.5h15M6 5.5h12a1.5 1.5 0 011.5 1.5v11h-15v-11A1.5 1.5 0 016 5.5z",
        document: "M7 3.5h6.2L18 8.2V20H7V3.5zm6 0v5h5",
        person: "M12 12.2a3.2 3.2 0 100-6.4 3.2 3.2 0 000 6.4zM6.2 19.2a5.8 5.8 0 0111.6 0",
        alert: "M12 4.2l7.2 13.2H4.8L12 4.2zm0 4.6v4.2m0 1.8h.01",
        tasks: "M9 7h9M9 12h9M9 17h9M5.5 7h.01M5.5 12h.01M5.5 17h.01",
    };

    if (!paths[name]) {
        return null;
    }

    return (
        <View style={styles.summaryIconCircle}>
            <Svg style={styles.summaryIcon} viewBox="0 0 24 24">
                <Path d={paths[name]} stroke={ICON_COLOR} strokeWidth={1.6} fill="none" />
            </Svg>
        </View>
    );
};

const STATUS_PILLS = {
    "#4CD4AC": { color: "#1F8F6E", backgroundColor: "#E5F8F1" },
    "#FFEE93": { color: "#8A6A12", backgroundColor: "#FFF6D8" },
    "#74B8FF": { color: "#2C6CB3", backgroundColor: "#E7F3FF" },
    "#BE4F59": { color: "#BE4F59", backgroundColor: "#FDECEE" },
};

const ProgressRing = ({ value = 0, color = "#FF4949" }) => {
    const clamped = Math.max(0, Math.min(100, Number(value) || 0));
    const endAngle = (clamped / 100) * 359.9;

    return (
        <View style={styles.progressRing}>
            <Svg style={styles.progressRingSvg} viewBox="0 0 32 32">
                <Circle cx={16} cy={16} r={11} stroke="#E6E8EC" strokeWidth={3} fill="none" />
                {clamped >= 100 ? (
                    <Circle cx={16} cy={16} r={11} stroke={color} strokeWidth={3} fill="none" />
                ) : null}
                {clamped > 0 && clamped < 100 ? (
                    <Path
                        d={describeArc(16, 16, 11, 0, endAngle)}
                        stroke={color}
                        strokeWidth={3}
                        fill="none"
                    />
                ) : null}
            </Svg>
            <Text style={styles.progressRingText}>{`${clamped}%`}</Text>
        </View>
    );
};

const RISK_LEGEND = [
    { title: "Insignificant", color: "#4EB8B1" },
    { title: "Minor", color: "#59C94F" },
    { title: "Noticeable", color: "#FFA049" },
    { title: "Critical", color: "#FF4949" },
    { title: "Existence - threatening", color: "#822626" },
];

const CHART_TEXT_COLORS = {
    New: "#E0B000",
    "In Progress": "#6C78D8",
    Review: "#3AAA40",
    Closed: "#9AA0A6",
    Deleted: "#E08B8B",
};

const displayColor = (item = {}) => CHART_TEXT_COLORS[item.title] || item.color;

const legendItemsForRisk = (items = []) => {
    const byTitle = new Map(
        items.map((item) => [String(item.title || "").toLowerCase(), item])
    );
    const canonical = RISK_LEGEND.map((entry) => {
        const match = byTitle.get(entry.title.toLowerCase());

        return {
            ...entry,
            amount: Number(match?.amount || 0),
            color: match?.color || entry.color,
        };
    });
    const extras = items.filter((item) => !byTitleHasCanonical(item));

    if (items.length && canonical.every((item) => !item.amount) && extras.length) {
        return items;
    }

    return canonical.concat(extras);
};

const byTitleHasCanonical = (item) => RISK_LEGEND.some(
    (entry) => entry.title.toLowerCase() === String(item.title || "").toLowerCase()
);

const DonutChart = ({
    items = [],
    t,
    centerText,
    legendItems,
    legendColumns = 1,
    counts = null,
}) => {
    const itemsWithPercentages = getPercentages(items);
    const hasData = itemsWithPercentages.some((item) => item.amount > 0);
    const chartItems = hasData ? itemsWithPercentages.filter((item) => item.percentage > 0) : [];
    let currentAngle = 0;
    const legend = legendItems || itemsWithPercentages;

    return (
        <View>
            <View style={styles.donutWrap}>
                <Svg style={styles.donutSvg} viewBox="0 0 104 104">
                    <Circle
                        cx={52}
                        cy={52}
                        r={36}
                        stroke="#E6E7EB"
                        strokeWidth={12}
                        fill="none"
                    />
                    {chartItems.map((item) => {
                        const sliceAngle = Math.min((item.percentage / 100) * 360, 359.9);
                        const path = describeArc(52, 52, 36, currentAngle, currentAngle + sliceAngle);
                        currentAngle += sliceAngle;
                        const stroke = displayColor(item);

                        return (
                            <Path
                                key={item.title}
                                d={path}
                                stroke={stroke}
                                strokeWidth={12}
                                fill="none"
                            />
                        );
                    })}
                </Svg>
                <Text style={styles.donutCenterValue}>{centerText}</Text>
            </View>

            <View style={legendColumns > 1 ? styles.legendGrid : undefined}>
                {legend.map((item) => (
                    <View
                        key={item.title}
                        style={legendColumns > 1 ? styles.legendGridItem : styles.legendItem}
                    >
                        <View style={[styles.legendDot, { backgroundColor: displayColor(item) }]} />
                        <Text style={styles.legendLabel}>{t(item.title)}</Text>
                    </View>
                ))}
            </View>

            {counts?.length ? (
                <View style={styles.taskCountRow}>
                    {counts.map((item) => (
                        <View key={item.title} style={styles.taskCountItem}>
                            <Text style={[styles.taskCountValue, { color: displayColor(item) }]}>
                                {String(item.amount || 0)}
                            </Text>
                            <Text style={[styles.taskCountLabel, { color: displayColor(item) }]}>
                                {t(item.title)}
                            </Text>
                        </View>
                    ))}
                </View>
            ) : null}
        </View>
    );
};

const SummaryField = ({ item, t }) => {
    const pill = STATUS_PILLS[(item.statusColor || "").toUpperCase()] || {
        color: "#4B5563",
        backgroundColor: "#F3F4F6",
    };
    const showStatusPill = item.type === "status" && item.value && item.value !== "-";

    return (
        <View style={styles.summaryField}>
            {item.icon ? <SummaryIcon name={item.icon} /> : null}
            <View style={styles.summaryFieldBody}>
                <Text style={styles.summaryFieldLabel}>{t(item.title)}</Text>
                {item.type === "percent" ? null : showStatusPill ? (
                    <Text style={[styles.summaryStatusPill, pill]}>
                        {String(item.value).toUpperCase()}
                    </Text>
                ) : (
                    <Text style={styles.summaryFieldValue}>
                        {formatDisplayValue(item.value)}
                    </Text>
                )}
            </View>
            {item.type === "percent" ? (
                <ProgressRing value={item.value} color={item.ringColor} />
            ) : null}
        </View>
    );
};

const PencilIcon = () => (
    <Svg style={styles.activityIcon} viewBox="0 0 24 24">
        <Path
            d="M3 17.25V21h3.75L17.8 9.94l-3.75-3.75L3 17.25zm17.71-10.04a1.004 1.004 0 000-1.42l-2.5-2.5a1.004 1.004 0 00-1.42 0l-1.96 1.96 3.75 3.75 2.13-1.79z"
            fill="#1F2937"
        />
    </Svg>
);

const toRadians = (angle) => (angle * Math.PI) / 180;

const polarToCartesian = (centerX, centerY, radius, angleInDegrees) => {
    const angleInRadians = toRadians(angleInDegrees - 90);

    return {
        x: centerX + radius * Math.cos(angleInRadians),
        y: centerY + radius * Math.sin(angleInRadians),
    };
};

const describeArc = (x, y, radius, startAngle, endAngle) => {
    const start = polarToCartesian(x, y, radius, endAngle);
    const end = polarToCartesian(x, y, radius, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";

    return [
        "M", start.x, start.y,
        "A", radius, radius, 0, largeArcFlag, 0, end.x, end.y,
    ].join(" ");
};


const RecentActivityList = ({ activities = [], dateUtils, t }) => {
    if (!activities.length) {
        return <Text style={styles.emptyStateText}>{t("No Data")}</Text>;
    }

    return (
        <>
            {activities.slice(0, 5).map((activity, index) => (
                <View key={activity?.id || index} style={styles.activityItem}>
                    <View style={styles.activityRow}>
                        <PencilIcon />
                        <View style={styles.activityBody}>
                            <Text style={styles.activityDate}>
                                {activity?.created_at
                                    ? `${dateUtils.convertDateToDate(activity.created_at, INTERNATIONAL_DATE_FORMAT)} ${dateUtils.convertDateToDate(activity.created_at, "HH:mm")}`
                                    : "-"}
                            </Text>
                            <Text style={styles.activityText}>
                                {activity?.text
                                    ? t(activity.text)
                                    : t(logDescriptionCreator.createFromApiData(activity) || "Update available")}
                            </Text>
                        </View>
                    </View>
                </View>
            ))}
        </>
    );
};

const RiskMatrix = ({ riskMatrixData, t }) => {
    const matrix = riskMatrixData?.matrix;
    const consequences = riskMatrixData?.consequences || [];
    const likelihoods = riskMatrixData?.likelihoods || [];

    if (!matrix?.length) {
        return null;
    }

    const renderRiskBullets = (count = 0) => {
        const visibleCount = Math.min(count, 10);

        return Array.from({ length: visibleCount }).map((_, index) => (
            <View key={index} style={styles.matrixRiskBullet} />
        ));
    };

    return (
        <View wrap={false}>
            <Text style={styles.riskMatrixTitle}>{t("Risk Matrix")}</Text>

            <View style={styles.riskMatrixTableWrap}>
                <View style={styles.riskMatrixTable}>
                <View style={styles.riskMatrixHeaderRow}>
                    <View style={styles.riskMatrixAxisLabelCell} />
                    <View style={styles.riskMatrixAxisValueCell} />
                    <View style={styles.riskMatrixConsequenceBand}>
                        <Text style={styles.riskMatrixConsequenceText}>{t("Consequence")}</Text>
                    </View>
                </View>

                <View style={styles.riskMatrixHeaderRow}>
                    <View style={styles.riskMatrixAxisLabelCell} />
                    <View style={styles.riskMatrixAxisValueCell} />
                    {consequences.map((consequence) => (
                        <View key={consequence.id} style={styles.riskMatrixXAxisCell}>
                            <Text style={styles.riskMatrixXAxisText}>
                                {`> ${(Number(consequence?.title || 0) / 1000).toLocaleString("en-US")}K`}
                            </Text>
                        </View>
                    ))}
                </View>

                <View style={styles.riskMatrixBodyRow}>
                    <View
                        style={[
                            styles.riskMatrixLikelihoodAxisLabelColumn,
                            { height: matrix.length * 47 },
                        ]}
                    >
                        <Text style={styles.riskMatrixLikelihoodText}>{t("Likelihood")}</Text>
                    </View>

                    <View style={styles.riskMatrixDataRows}>
                        {matrix.map((row, rowIndex) => (
                            <View key={rowIndex} style={styles.riskMatrixRow}>
                                <View style={styles.riskMatrixYAxisCell}>
                                    <Text style={styles.riskMatrixYAxisText}>
                                        {`> ${likelihoods[rowIndex]?.label || 0}%`}
                                    </Text>
                                </View>

                                {row.map((cell) => {
                                    const riskCount = cell?.risks?.length || 0;

                                    return (
                                        <View
                                            key={cell.id}
                                            style={[
                                                styles.riskMatrixCell,
                                                { backgroundColor: `#${cell?.color || "FFFFFF"}` },
                                            ]}
                                        >
                                            <View style={styles.matrixRiskCellBottom}>
                                                <View style={styles.matrixBulletsWrap}>
                                                    {riskCount > 0 && renderRiskBullets(riskCount)}
                                                </View>
                                                {riskCount > 0 && (
                                                    <View style={styles.matrixCountWrap}>
                                                        <Text style={styles.riskMatrixCellText}>{riskCount}</Text>
                                                    </View>
                                                )}
                                            </View>
                                        </View>
                                    );
                                })}
                            </View>
                        ))}
                    </View>
                </View>
            </View>
            </View>
        </View>
    );
};

const ManagementSummarySection = ({
    t,
    supplierData,
    overviewHeader,
    statistics,
    lastActivities,
    riskMatrixData,
    isOwnCompany,
    meta,
}) => {
    const dateUtils = useMemo(() => new DateUtils(), []);
    const resolvedOverviewHeader = overviewHeader || supplierData?.overviewHeader || {};
    const resolvedStatistics = statistics || supplierData?.statistics || {};
    const resolvedLastActivities = lastActivities || supplierData?.lastActivities || [];
    const resolvedRiskMatrixData = riskMatrixData || supplierData?.riskMatrix || null;

    const summaryData = useMemo(
        () => buildManagementSummaryData({
            supplierData,
            overviewHeader: resolvedOverviewHeader,
            statistics: resolvedStatistics,
            lastActivities: resolvedLastActivities,
            riskMatrixData: resolvedRiskMatrixData,
            dateUtils,
            t,
            isOwnCompany,
        }),
        [
            supplierData,
            resolvedOverviewHeader,
            resolvedStatistics,
            resolvedLastActivities,
            resolvedRiskMatrixData,
            dateUtils,
            t,
            isOwnCompany,
        ]
    );

    const taskItems = summaryData.tasks || [];
    const taskTotal = taskItems.reduce((sum, item) => sum + Number(item.amount || 0), 0);
    const dominantTask = taskItems.reduce(
        (best, item) => (Number(item.amount || 0) > Number(best?.amount || 0) ? item : best),
        taskItems[0]
    );
    const taskCenter = taskTotal
        ? `${Math.round((Number(dominantTask?.amount || 0) / taskTotal) * 100)}%`
        : t("No Data");
    const riskHasData = (summaryData.riskLevels || []).some((item) => Number(item.amount || 0) > 0);
    const taskCounts = taskItems.filter((item) => item.title !== "Deleted");

    return (
        <View style={styles.managementSummaryContent}>
            <PageReference id={meta?.anchorId} />
            <Text style={styles.managementSummaryHeading}>
                {t("Management Summary").toUpperCase()}
            </Text>
            <View style={styles.managementSummaryUnderline} />

            <View style={styles.managementSummaryBox}>
                <View style={styles.summaryMetricsRow}>
                    {summaryData.firstRow.map((item) => (
                        <SummaryField key={item.title} item={item} t={t} />
                    ))}
                </View>
                <View style={[styles.summaryMetricsRow, styles.summaryMetricsRowDivider]}>
                    {summaryData.secondRow.map((item) => (
                        <SummaryField key={item.title} item={item} t={t} />
                    ))}
                </View>
            </View>

            <View style={styles.analyticsCards}>
                <View style={styles.analyticsCard}>
                    <Text style={styles.analyticsTitle}>
                        {t(isOwnCompany ? "Company Risk Level" : "Third Party Risk Level")}
                    </Text>
                    <DonutChart
                        items={riskHasData ? summaryData.riskLevels : []}
                        legendItems={legendItemsForRisk(summaryData.riskLevels)}
                        centerText={riskHasData ? "" : t("No Data")}
                        t={t}
                    />
                </View>

                <View style={styles.analyticsCard}>
                    <Text style={styles.analyticsTitle}>{t("Tasks")}</Text>
                    <DonutChart
                        items={taskItems}
                        centerText={taskCenter}
                        legendColumns={2}
                        counts={taskCounts}
                        t={t}
                    />
                </View>

                <View style={[styles.analyticsCard, styles.analyticsCardLast]}>
                    <Text style={styles.analyticsTitle}>{t("Recent Activity")}</Text>
                    <RecentActivityList
                        activities={summaryData.recentActivities}
                        dateUtils={dateUtils}
                        t={t}
                    />
                </View>
            </View>

            <RiskMatrix riskMatrixData={summaryData.riskMatrixData} t={t} />
        </View>
    );
};

export default withNamespaces()(ManagementSummarySection);
